import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  ConnectionChangeDto,
  ConnectionChangeRequestTypeDisplayValues,
  ConnectionChangeRequestTypeEnum
} from '@bo-client/shared/models/connectionChange.dto';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderRequestWizardService } from '@bo-client/nap/order-request-wizard/order-request-wizard.service';
import { catchError, combineLatestWith, map, shareReplay, switchMap } from 'rxjs/operators';
import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import { TypedForm } from '@bo-client/shared/forms/generic-forms/typedForm.type';
import { ValidatorsService } from '@bo-client/shared/forms/validators.service';
import { ToastrService } from 'ngx-toastr';
import { ArticleCase, articles } from '@bo-client/shared/constants/articles';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { path } from '@bo-client/shared/constants/path';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { ConnectionChangeRequestTypeConfig } from '@bo-client/shared/config.model';

@Component({
  selector: 'app-order-request-wizard-offer',
  templateUrl: './order-request-wizard-offer.component.html',
  styleUrl: './order-request-wizard-offer.component.scss'
})
export class OrderRequestWizardOfferComponent implements OnInit {
  private dataShareService = inject(DataShareService);
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private orderRequestWizardService = inject(OrderRequestWizardService);
  private validatorsService = inject(ValidatorsService);
  private formBuilder = inject(FormBuilder);
  private toastService = inject(ToastrService);

  model$: Observable<ConnectionChangeDto | null> = of(null);
  info$: Observable<string | null> = of(null);
  token: string = null;
  subject$: Observable<string> = of(null);
  offerForm: TypedForm<{
    acceptDataAccuracy: boolean;
    acceptOfferConditions: boolean;
  }>;
  isFreeOfCharge$: Observable<boolean> = of(false);

  configCheckboxData$: Observable<{
    form: FormArray<FormControl<boolean>>;
    texts: string[];
  }>;

  ngOnInit() {
    this.offerForm = this.formBuilder.group({
      acceptDataAccuracy: [false, this.validatorsService.checkboxRequiredValidator()],
      acceptOfferConditions: [false, this.validatorsService.checkboxRequiredValidator()]
    });

    this.model$ = this.route.paramMap.pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap((params) => {
        if (!params.has('id') || !params.has('token')) {
          return of(null);
        }
        this.token = params.get('token');
        return this.orderRequestWizardService.getEntityAsExternal(params.get('id'), params.get('token')).pipe(
          catchError((error) => {
            // eslint-disable-next-line no-console
            console.error(
              'Error fetching offer entity with id ' +
                params.get('id') +
                ' and token ' +
                params.get('token') +
                ':',
              error
            );
            return of(null);
          }),
          map((response) => {
            if (response) {
              return response.entity;
            }
            return null;
          })
        );
      }),
      shareReplay(1)
    );
    this.isFreeOfCharge$ = this.model$.pipe(
      map((model) => {
        return !(
          model.totalPriceNetto ||
          model.positions.reduce((currentPrice, position) => currentPrice + position.amount, 0) > 0
        );
      })
    );
    this.subject$ = this.model$.pipe(
      map((model) => {
        if (model && model.connectionChangeRequestType) {
          return ConnectionChangeDto.GET_FORMAL_SUBJECT_NAME(
            model.connectionChangeRequestType,
            ArticleCase.dat
          );
        }
        return null;
      }),
      shareReplay(1)
    );
    this.info$ = this.model$.pipe(
      map((model) => {
        return (model && model.additionalOfferText) || null;
      }),
      shareReplay(1)
    );

    this.initConfigCheckboxes();
  }

  submit(data: ConnectionChangeDto, checkBoxForm?: FormArray<FormControl<boolean>>) {
    if (this.offerForm.invalid || checkBoxForm?.invalid) {
      this.toastService.error(
        'Bitte beachten Sie die rot markierten Felder und befolgen Sie die Hinweistexte.',
        'Formular ungültig'
      );
      this.offerForm.markAllAsTouched();
      checkBoxForm?.markAllAsTouched();
      return;
    }
    const resultObservable = this.orderRequestWizardService.submitAcceptOffer(data, this.token);

    resultObservable.subscribe({
      next: () => {
        const subjectType = ConnectionChangeRequestTypeDisplayValues[data.connectionChangeRequestType];
        const subject = `${articles.upper.formal[subjectType?.gender].acc} ${
          data.connectionChangeRequestType === ConnectionChangeRequestTypeEnum.TEMPORARY
            ? 'temporären Anschluss'
            : subjectType?.name
        }`;

        this.toastService.success('Vielen Dank für Ihre Bestellung.');
        const message = `Wir haben Ihren Antrag für ${subject} erhalten. Sie werden in Kürze eine Bestätigungsmail mit allen Informationen zu Ihrer Bestellung erhalten.`;
        this.router
          .navigate([path.external + '/' + path.goToThanksPage + '/' + message], {
            replaceUrl: true
          })
          .then();
      },
      error: (err) => {
        this.toastService.error(
          'Beim Übermitteln ist ein Fehler aufgetreten: ' + err.status + ' ' + err.error
        );
      }
    });
  }

  private initConfigCheckboxes() {
    this.configCheckboxData$ = this.dataShareService
      .getSingleTenantConfig$<ConnectionChangeRequestTypeConfig[]>(
        'connectionChangeRequest.connectionChangeRequestTypes'
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        combineLatestWith(this.model$),
        map(([config, model]) => {
          if (!model) return [];
          return (
            ConnectionChangeRequestTypeConfig.getConnectionChangeRequestTypeConfig(
              model.area,
              model.connectionChangeRequestType,
              config
            )?.offerCheckBoxes || []
          );
        }),
        map((checkBoxes) => {
          return {
            form: this.formBuilder.array(
              checkBoxes.map(() => [false, this.validatorsService.checkboxRequiredValidator()])
            ),
            texts: checkBoxes.map((item) => item.checkBoxText)
          };
        }),
        shareReplay(1)
      );
  }

  protected readonly ConnectionChangeRequestTypeEnum = ConnectionChangeRequestTypeEnum;
}
